/**
 * Captures the browser's install events before Angular boots.
 *
 * Chromium fires `beforeinstallprompt` once per page load, as soon as the install criteria are
 * met — which can be before the app shell (and so `PwaInstallService`) exists. Missing it means
 * the "Install app" button has nothing to call `prompt()` on until the next reload, so
 * `startPwaInstallCapture()` is called from `main.ts` before `bootstrapApplication` and holds on
 * to the event until the service subscribes.
 *
 * Plain module state on purpose: no DI exists yet at the time this runs.
 */

/** Chromium-only event; not in lib.dom.d.ts. */
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: readonly string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

export interface PwaInstallCaptureListener {
  onPromptAvailable(event: BeforeInstallPromptEvent): void;
  onInstalled(): void;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let hasInstalled: boolean = false;
let listener: PwaInstallCaptureListener | null = null;
let isStarted: boolean = false;

export function startPwaInstallCapture(target: Window = window): void {
  if (isStarted || typeof target?.addEventListener !== 'function') return;
  isStarted = true;

  target.addEventListener('beforeinstallprompt', (event: Event) => {
    // Suppress Chrome's own mini-infobar: we offer install at a moment of our choosing.
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    listener?.onPromptAvailable(deferredPrompt);
  });

  target.addEventListener('appinstalled', () => {
    hasInstalled = true;
    deferredPrompt = null;
    listener?.onInstalled();
  });
}

/** Subscribes the (single) consumer and replays anything captured before it existed. */
export function connectPwaInstallCapture(next: PwaInstallCaptureListener): void {
  listener = next;
  if (deferredPrompt) next.onPromptAvailable(deferredPrompt);
  if (hasInstalled) next.onInstalled();
}

/** A deferred prompt can be shown only once; the consumer calls this after using it. */
export function clearCapturedInstallPrompt(): void {
  deferredPrompt = null;
}

/** Test-only: reset module state between specs. */
export function resetPwaInstallCaptureForTests(): void {
  deferredPrompt = null;
  hasInstalled = false;
  listener = null;
  isStarted = false;
}
